import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  Grid
} from '@mui/material';
import config from '../config';
import { getValidToken } from '../services/tokenRefreshService';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function StoreEarlyCutoffEdit() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const [storeName, setStoreName] = useState('');
  const [cutoffs, setCutoffs] = useState<Record<number, string>>({});
  const [defaultCutoffs, setDefaultCutoffs] = useState<Record<number, string>>({});
  const [useDefaults, setUseDefaults] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEarlyCutoffs();
  }, [storeId]);

  const fetchEarlyCutoffs = async () => {
    const token = await getValidToken();

    // Fetch store info
    const storeRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const storeData = await storeRes.json();
    setStoreName(storeData.store.name);

    // Fetch store-specific cutoffs
    const storeCutoffsRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/early-cutoffs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const storeCutoffsData = await storeCutoffsRes.json();

    // Fetch system default cutoffs
    const defaultsRes = await fetch(`${config.API_URL}/admin/stores/defaults`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const defaultsData = await defaultsRes.json();

    // Convert arrays to objects keyed by dayOfWeek
    const storeCutoffsObj: Record<number, string> = {};
    storeCutoffsData.forEach((c: any) => {
      storeCutoffsObj[c.dayOfWeek] = c.cutoffTime.substring(0, 5);
    });

    const defaultCutoffsObj: Record<number, string> = {};
    defaultsData.defaultEarlyCutoffs.forEach((c: any) => {
      defaultCutoffsObj[c.dayOfWeek] = c.cutoffTime.substring(0, 5);
    });

    // Determine which days use defaults (no store-specific cutoffs)
    const useDefaultsObj: Record<number, boolean> = {};
    const finalCutoffs: Record<number, string> = {};

    for (let day = 0; day < 7; day++) {
      if (storeCutoffsObj[day]) {
        // Store has specific cutoff for this day
        useDefaultsObj[day] = false;
        finalCutoffs[day] = storeCutoffsObj[day];
      } else {
        // No store-specific cutoff - use defaults
        useDefaultsObj[day] = true;
        finalCutoffs[day] = defaultCutoffsObj[day] || '';
      }
    }

    setDefaultCutoffs(defaultCutoffsObj);
    setCutoffs(finalCutoffs);
    setUseDefaults(useDefaultsObj);
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const token = await getValidToken();

    // Only include cutoffs for days that don't use defaults AND have a cutoff time set
    const earlyCutoffs = Object.keys(cutoffs)
      .filter(dayOfWeek => !useDefaults[parseInt(dayOfWeek)] && cutoffs[parseInt(dayOfWeek)])
      .map(dayOfWeek => ({
        dayOfWeek: parseInt(dayOfWeek),
        cutoffTime: `${cutoffs[parseInt(dayOfWeek)]}:00`,
      }));

    await fetch(`${config.API_URL}/admin/stores/${storeId}/early-cutoffs`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ earlyCutoffs }),
    });

    setSaving(false);
    alert('Liquor times saved!');
  };

  const handleToggleDefault = (day: number, checked: boolean) => {
    setUseDefaults({ ...useDefaults, [day]: checked });
    if (checked) {
      // Switch to default cutoff
      setCutoffs({
        ...cutoffs,
        [day]: defaultCutoffs[day] || ''
      });
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Store Liquor Times: {storeName}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Set times when restricted categories (e.g., liquor) stop selling. Use system defaults or set store-specific times. Leave blank for no cutoff.
        </Alert>

        {DAYS.map((day, index) => (
          <Box key={index} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useDefaults[index] || false}
                  onChange={(e) => handleToggleDefault(index, e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">{day}</Typography>
                  {defaultCutoffs[index] ? (
                    <Typography variant="body2" color="text.secondary">
                      System default: {defaultCutoffs[index]}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      System default: No cutoff
                    </Typography>
                  )}
                </Box>
              }
            />
            <TextField
              label="Liquor Cutoff Time (optional)"
              type="time"
              value={cutoffs[index] || ''}
              onChange={(e) => setCutoffs({
                ...cutoffs,
                [index]: e.target.value
              })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={useDefaults[index]}
              helperText={
                useDefaults[index]
                  ? defaultCutoffs[index]
                    ? `Using system default: ${defaultCutoffs[index]}`
                    : 'Using system default: No cutoff'
                  : cutoffs[index]
                    ? `Restricted categories stop selling at ${cutoffs[index]}`
                    : 'No liquor cutoff for this day'
              }
              sx={{ mt: 1 }}
            />
          </Box>
        ))}

        <Box sx={{ marginTop: '20px', display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/stores')}
          >
            ← Back to Stores
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/stores/${storeId}/hours`)}
          >
            Manage Working Hours
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Liquor Times'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
